import { useState, useCallback, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom'; 
import { useTranslation } from 'react-i18next';
import { MapPin, Menu, ChevronDown, X, Loader2, User, LogIn, LogOut } from 'lucide-react';
import { resetTemporaryCoordinates } from '../../slices/locationSlice';
import { logoutUser } from '../../slices/authSlice';
import LoginPromptBanner from '../UIFeedback/LoginPromptBanner/LoginPromptBanner';
import HeaderSearchPanel from '../HeaderSearchPanel';
import LocationDropdown from '../LocationDropdown';
import { cn } from '../../lib/utils';
import Logo from '../Logo/Logo';
import BrandName from '../BrandName/BrandName';

const Header = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const locationName = useSelector((state) => state.location.locationName);
  const tempCoordinates = useSelector((state) => state.location.temporaryCoordinates);
  const tempLocationName = useSelector((state) => state.location.tempLocationName);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [showMenuDropdown, setShowMenuDropdown] = useState(false);
  const menuRef = useRef(null);

  // Close the menu dropdown on outside click or Escape
  useEffect(() => {
    if (!showMenuDropdown) return;

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenuDropdown(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') setShowMenuDropdown(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showMenuDropdown]);

  const handleAccountClick = useCallback(() => {
    setShowMenuDropdown(false);
    navigate('/profile');
  }, [navigate]);

  const handleLoginClick = useCallback(() => {
    setShowMenuDropdown(false);
    navigate('/login');
  }, [navigate]);

  const handleLogoutClick = useCallback(() => {
    setShowMenuDropdown(false);
    dispatch(logoutUser());
    navigate('/');
  }, [dispatch, navigate]);

  const handleLocationClick = useCallback(() => {
    // Toggle location dropdown - available for all users
    setShowLocationDropdown((prev) => !prev);
  }, []);

  const handleCloseLocationDropdown = useCallback(() => {
    setShowLocationDropdown(false);
  }, []);

  const handleResetLocation = async (e) => {
    e.stopPropagation();
    setIsResetting(true);
    await new Promise((resolve) => setTimeout(resolve, 500));
    dispatch(resetTemporaryCoordinates());
    setIsResetting(false);
  };

  return (
    <header
      className={cn(
        "fixed left-0 right-0 z-50",
        "w-full",
        "bg-white",
        "border-b border-border"
      )}
      style={{ top: 'var(--account-banner-h, 0px)' }}
    >
      <div className="flex items-center justify-between px-3 sm:px-4 md:px-6 py-3 sm:py-4 min-h-[70px] sm:min-h-[80px] md:min-h-[90px]">
        {/* Left side - Logo, Welcome, and Location */}
        <div className="flex items-center gap-2 sm:gap-3 md:gap-4 flex-1 min-w-0">
          {/* Logo */}
          <Logo
            className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 flex-shrink-0"
            showTrademark={false}
          />
          
          {/* Welcome and Location */}
          <div className="flex flex-col min-w-0 flex-1 justify-center relative">
            <h1 className="text-base sm:text-md md:text-xl font-bold text-foreground truncate leading-tight">
              {isAuthenticated && user
                ? (user.firstName
                    ? t('welcome_user', { firstName: user.firstName, defaultValue: 'Welcome, {{firstName}}!' })
                    : t('welcome_generic', 'Welcome!'))
                : <BrandName />
              }
            </h1>
            
            {/* Location Button */}
            <div className="relative">
              {/* Regular Location - when no temp location */}
              {!tempCoordinates && (
                <button
                  onClick={handleLocationClick}
                  className={cn(
                    "flex items-center gap-1 hover:bg-gray-50 rounded px-1 -ml-2 transition-colors",
                    showLocationDropdown && "bg-gray-50"
                  )}
                >
                  <MapPin className="w-4 h-4 text-[#595959] flex-shrink-0" />
                  <span className="text-sm text-[#595959] truncate max-w-[170px] sm:max-w-[250px] md:max-w-[310px] lg:max-w-none">
                    {locationName || t('fetching_location', 'Fetching location...')}
                  </span>
                  <ChevronDown className={cn(
                    "w-4 h-4 text-[#595959] flex-shrink-0 transition-transform duration-200",
                    showLocationDropdown && "rotate-180"
                  )} />
                </button>
              )}

              {/* Temporary Location */}
              {tempCoordinates && (
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <button
                    onClick={handleLocationClick}
                    className={cn(
                      "flex items-center gap-1 px-2 py-1 bg-blue-50 hover:bg-blue-100 rounded-full transition-colors",
                      showLocationDropdown && "bg-blue-100"
                    )}
                  >
                    <MapPin className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                    <span className="text-xs sm:text-sm font-medium text-blue-700 truncate max-w-[130px] sm:max-w-[200px] md:max-w-[260px]">
                      {tempLocationName || t('temporary_location', 'Temporary Location')}
                    </span>
                    <ChevronDown className={cn(
                      "w-3.5 h-3.5 text-blue-600 flex-shrink-0 transition-transform duration-200",
                      showLocationDropdown && "rotate-180"
                    )} />
                  </button>
                  <button
                    onClick={handleResetLocation}
                    disabled={isResetting}
                    className="flex items-center justify-center w-6 h-6 sm:w-auto sm:h-auto sm:gap-1 sm:px-2 sm:py-0.5 bg-gray-100 hover:bg-gray-200 rounded-full text-xs font-medium text-gray-600 transition-colors"
                    aria-label={t('reset_location', 'Reset location')}
                  >
                    {isResetting ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <X className="w-3 h-3" />
                    )}
                    <span className="hidden sm:inline">{t('reset', 'Reset')}</span>
                  </button>
                </div>
              )}

              {/* Location Dropdown */}
              <LocationDropdown
                isOpen={showLocationDropdown}
                onClose={handleCloseLocationDropdown}
              />
            </div>
          </div>
        </div>

        {/* Right side - Search and Menu Buttons */}
        <div className="flex items-center gap-1 sm:gap-2 md:gap-3 flex-shrink-0">
          {/* Desktop Inline Search */}
          <div className="hidden lg:block">
            <HeaderSearchPanel
              isOpen={true}
              onClose={() => {}}
              variant="inline"
            />
          </div>

          {/* Menu Button + Dropdown */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowMenuDropdown((prev) => !prev)}
              className={cn(
                "p-1.5 sm:p-2 md:p-2.5",
                "rounded-lg",
                "transition-all duration-300",
                "hover:bg-muted",
                "text-foreground",
                "flex items-center justify-center",
                "min-w-[36px] min-h-[36px] sm:min-w-[44px] sm:min-h-[44px]",
                showMenuDropdown && "bg-muted"
              )}
              aria-label={t('menu', 'Menu')}
              aria-haspopup="menu"
              aria-expanded={showMenuDropdown}
            >
              <Menu className="w-5 h-5 md:w-6 md:h-6" />
            </button>

            {showMenuDropdown && (
              <div
                role="menu"
                className={cn(
                  "absolute right-0 top-full mt-2 z-50",
                  "w-48 bg-white rounded-xl shadow-lg border border-gray-200",
                  "py-1.5",
                  "animate-in fade-in slide-in-from-top-2 duration-200"
                )}
              >
                <button
                  role="menuitem"
                  onClick={handleAccountClick}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-secondary/40 transition-colors"
                >
                  <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span>{t('menu', 'Menu')}</span>
                </button>

                <div className="my-1 border-t border-border" />

                {isAuthenticated ? (
                  <button
                    role="menuitem"
                    onClick={handleLogoutClick}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="w-4 h-4 flex-shrink-0" />
                    <span>{t('logout', 'Logout')}</span>
                  </button>
                ) : (
                  <button
                    role="menuitem"
                    onClick={handleLoginClick}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-secondary/40 transition-colors"
                  >
                    <LogIn className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span>{t('login', 'Login')}</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Login Prompt Banner */}
      <LoginPromptBanner
        show={showLoginPrompt}
        setShow={setShowLoginPrompt}
        actionText={t('change_location_action')}
      />
    </header>
  );
};

export default Header;
